<script lang="ts">
  import { initUserContext } from '#lib/context.svelte.ts';
  import AppIcon from '#lib/gui/apple-touch-icon.png';
  import AuthCheck from '#lib/gui/AuthCheck.svelte';
  import BottomNav from '#lib/gui/BottomNav.svelte';
  import Navigation from '#lib/gui/Navigation.svelte';
  import { Login, LoginRecaptcha } from '#lib/gui/signin/index.ts';
  import { ignore, wait } from '@yp/common/async.ts';
  import { isAbsent } from '@yp/common/identity.ts';
  import { Seconds } from '@yp/common/units.ts';
  import { Orientation, screenSize } from '@yp/web.app/client/device.ts';
  import { PwaInstaller } from '@yp/web.app/client/pwa/install.ts';
  import { setOrientationLock } from '@yp/web.app/client/screen.ts';
  import { Button } from '@yp/web.app/components/index.ts';
  import { initCallbacks } from '@yp/web.app/context.ts';
  import { InstallScreen } from '@yp/web.app/gui/install_screen.ts';
  import { Toaster } from '@yp/web.app/gui/toaster.ts';
  import { refreshToken } from '@yp/web.firebase/client/auth.ts';
  import { type SessionType } from '@yp/web.firebase/context.ts';
  import { DEV } from 'esm-env';
  import { onMount, type Snippet } from 'svelte';
  import { Modals } from 'svelte-modals';
  import { CanInstall } from '../../config/index.ts';
  import type { PageProps } from './$types.ts';

  initCallbacks();

  const assumeInstalled = DEV;

  const { data, children }: PageProps & { children: Snippet } = $props();

  const showInstall = $derived($CanInstall && !assumeInstalled);

  // $: section = getSection($page.url.pathname);

  onMount(() => {
    const screen = screenSize();
    console.log('Display', { screen });

    ignore(setOrientationLock('portrait'));

    ignore(
      Orientation.load().then((o) => {
        console.log('orientation', o);
      }),
    );
  });

  const uctx = initUserContext();

  export function needsTokenRefresh(session: SessionType | undefined) {
    return false;
  }

  let canRefresh: true | false | 'pending' = true;
  $effect(() => {
    if (isAbsent(uctx.user)) {
      canRefresh = true;
      return;
    }
    if (!(needsTokenRefresh(uctx.session) && canRefresh === true)) {
      return;
    }
    canRefresh = 'pending';
    console.warn('Permissions changed. Updating token.');
    ignore(
      refreshToken(uctx.user!)
        .then(() => wait(Seconds(10)))
        .then(() => {
          if (needsTokenRefresh(uctx.session)) {
            console.warn('Refreshed token still out of date.');
            // This will get reenabled on the next logout.
            canRefresh = false;
          } else {
            canRefresh = true;
          }
        }),
    );
  });
</script>

<div class="h-dvh relative flex flex-col">
  {#if uctx.isSessionLoaded}
    {#if !showInstall}
      <Login />
      <LoginRecaptcha />
    {/if}

    {#if !showInstall && uctx.user}
      <div class="bg-black pb-1 sticky top-0 z-20">
        <Navigation version={data.version} />
      </div>
    {/if}

    <main class="grow flex flex-col overflow-x-clip overflow-y-auto">
      {#if showInstall}
        <InstallScreen
          version={data.version}
          icon={AppIcon}
          install={() => PwaInstaller.instance().install()} />
      {:else}
        <AuthCheck allowed={['/signin/']}>
          {#snippet needSignIn(signIn)}
            <div class="w-full">
              <div class="font-estilo text-4xl text-center my-16">
                <div>New Year's Eve</div>
                <div class="font-bold mt-2 text-5xl"><span class="sub">20</span>25</div>
              </div>
            </div>
            <div class="text-2xl flex flex-col items-stretch gap-4 w-full p-4 grow justify-between">
              <Button class="w-[70dvw] mx-auto" scheme="p" size="xl" onClick={signIn}
                >Sign in</Button>
              <div class="text-xs text-right text-base-content">
                <div class="inline-block bg-base-100 py-1 px-2 rounded-md border-base-300">
                  <span>Version {data.version}</span><span class="ml-2 italic font-bold">Beta</span>
                </div>
              </div>
            </div>
          {/snippet}

          {@render children()}
        </AuthCheck>
      {/if}
    </main>

    {#if !showInstall && uctx.isLoggedIn}
      <div class="bg-m-dp sticky bottom-0">
        <BottomNav />
      </div>
    {/if}
  {/if}
</div>

<Modals>
  {#snippet backdrop({ close })}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div class="backdrop" onclick={() => close()} />
  {/snippet}
</Modals>

<Toaster />

<style lang="scss">
  .backdrop {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    // Sigh, redundant with backdrop
    // background: rgba(0, 0, 0, 0.5);
  }
</style>
